import type { LucideIcon } from 'lucide-react';
import { type ReactElement, useId, useState } from 'react';

interface ToolbarButtonProps {
  readonly icon: LucideIcon;
  /** 아이콘 아래 짧은 이름(넓은 도구 줄에서 보인다). */
  readonly label: string;
  /** 화면낭독기·툴팁에 쓰는 온전한 이름 — 없으면 label. */
  readonly description?: string;
  readonly disabled?: boolean;
  /** 막혀 있을 때 툴팁·낭독기로 알리는 이유(예: 레이어를 선택하면 쓸 수 있어요). */
  readonly disabledHint?: string;
  readonly onClick: () => void;
}

interface TipPosition {
  readonly x: number;
  readonly y: number;
}

/**
 * 스테이지 위쪽 도구 줄의 한 칸 — 작은 사각 아이콘, 도구 줄이 넓으면 아래에 짧은 이름을 단다.
 * 마우스를 올리거나 포커스하면 이름(막혔으면 이유까지)을 툴팁으로 보인다. 툴팁은 화면 고정 위치라 가로 스크롤 줄에 잘리지 않는다.
 * 막힌 버튼은 포인터를 받지 않으므로 둘레 칸이 마우스를 받아 이유를 보인다.
 */
export const ToolbarButton = ({
  icon: Icon,
  label,
  description,
  disabled = false,
  disabledHint,
  onClick,
}: ToolbarButtonProps): ReactElement => {
  const [tip, setTip] = useState<TipPosition | null>(null);
  const hintId = useId();
  const name = description ?? label;
  const reason = disabled ? disabledHint : undefined;
  const show = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    setTip({ x: rect.left + rect.width / 2, y: rect.bottom + 6 });
  };
  const hide = () => setTip(null);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: 막힌 버튼은 포인터를 받지 않아 둘레 칸이 툴팁용 마우스 진입만 받는다(동작 없음).
    <span
      className="relative inline-flex shrink-0"
      onPointerEnter={(event) => show(event.currentTarget)}
      onPointerLeave={hide}
      onFocus={(event) => show(event.currentTarget)}
      onBlur={hide}
    >
      <button
        type="button"
        aria-label={name}
        aria-describedby={reason === undefined ? undefined : hintId}
        disabled={disabled}
        onClick={onClick}
        className="inline-flex h-9 min-w-9 shrink-0 cursor-pointer flex-col items-center justify-center gap-1 px-1 text-foreground transition-colors hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring disabled:pointer-events-none disabled:text-foreground/25 @3xl:h-12 @3xl:min-w-11"
      >
        <Icon aria-hidden className="size-[18px] shrink-0" strokeWidth={1.5} />
        <span aria-hidden className="hidden text-[10px] leading-none whitespace-nowrap @3xl:block">
          {label}
        </span>
      </button>
      {reason !== undefined && (
        <span id={hintId} className="sr-only">
          {reason}
        </span>
      )}
      {tip !== null && (
        <span
          role="tooltip"
          className="pointer-events-none fixed z-50 -translate-x-1/2 bg-foreground px-2 py-1 text-[11px] whitespace-nowrap text-background shadow-sm"
          style={{ left: tip.x, top: tip.y }}
        >
          {reason === undefined ? name : `${name} · ${reason}`}
        </span>
      )}
    </span>
  );
};
